/**
 * S-04 — Enrichissement BPML (Admin / Manager) — US-02, Lot 3b.
 * Permet l'édition inline de chaque ligne (tous attributs du template)
 * en plus de l'import Excel en masse.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  bpmlApi,
  domainsApi,
  applicationApi,
  applicationFeaturesApi,
  applicationEnvironmentsApi,
  ApiRequestError,
  type BpmlEnrichBulkReport,
  type BpmlLineDto,
  type DomainDto,
  type ApplicationDto,
  type ApplicationFeatureDto,
  type ApplicationEnvironmentDto,
} from "../api/api";
import { localeLabel } from "../utils/locale";

const CATEGORIES = ["Process", "Data", "Reporting", "Integration"] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

type EditState = {
  lineId: string;
  label: string;
  complexityIndex: number;
  isCritical: boolean;
  processCategory: string;
  /** features sélectionnées pour cette ligne */
  featureIds: string[];
  /** environnements sélectionnés pour cette ligne */
  environmentIds: string[];
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Groupe les features par application → module N1 pour le picker */
function groupForPicker(features: ApplicationFeatureDto[]) {
  const appMap = new Map<string, { appLabel: string; modules: Map<string, { modLabel: string | null; features: ApplicationFeatureDto[] }> }>();
  for (const f of features) {
    if (!appMap.has(f.applicationId)) appMap.set(f.applicationId, { appLabel: f.applicationLabel, modules: new Map() });
    const app = appMap.get(f.applicationId)!;
    const modKey = f.moduleCode ?? "__none__";
    if (!app.modules.has(modKey)) app.modules.set(modKey, { modLabel: f.moduleLabel, features: [] });
    app.modules.get(modKey)!.features.push(f);
  }
  return Array.from(appMap.values());
}

// ── Feature picker multi-select ───────────────────────────────────────────────

function FeaturePicker({
  allFeatures,
  selectedIds,
  onChange,
}: {
  allFeatures: ApplicationFeatureDto[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const selectedSet = new Set(selectedIds);
  const selectedFeatures = allFeatures.filter((f) => selectedSet.has(f.id));

  const filtered = search.trim()
    ? allFeatures.filter(
        (f) =>
          f.code.toLowerCase().includes(search.toLowerCase()) ||
          f.labelEn.toLowerCase().includes(search.toLowerCase()) ||
          (f.labelFr ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : allFeatures;

  const grouped = groupForPicker(filtered);

  function toggle(id: string) {
    onChange(selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  }

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
      {/* Selected chips */}
      {selectedFeatures.length > 0 && (
        <div style={{ padding: "6px 8px", borderBottom: "1px solid #e2e8f0", display: "flex", flexWrap: "wrap", gap: 4 }}>
          {selectedFeatures.map((f) => (
            <span key={f.id} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "#dbeafe", color: "#1e40af", borderRadius: 4,
              padding: "2px 6px", fontSize: "0.78em", fontFamily: "monospace",
            }}>
              {f.code}
              <button
                type="button"
                onClick={() => toggle(f.id)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#1e40af", padding: 0, lineHeight: 1, fontSize: "1em" }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search + list */}
      <div style={{ padding: "6px 8px", borderBottom: "1px solid #e2e8f0" }}>
        <input
          className="sm-input"
          placeholder="Rechercher un code ou libellé…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "100%" }}
        />
      </div>

      <div style={{ maxHeight: 200, overflowY: "auto", padding: "4px 0" }}>
        {grouped.length === 0 ? (
          <p className="sm-muted" style={{ padding: "8px 12px", fontSize: "0.85em" }}>Aucune feature disponible</p>
        ) : (
          grouped.map((app) => (
            <div key={app.appLabel}>
              <div style={{ padding: "4px 12px", fontWeight: 600, fontSize: "0.8em", color: "#374151", background: "#f8fafc", position: "sticky", top: 0 }}>
                {app.appLabel}
              </div>
              {Array.from(app.modules.values()).map((mod, mi) => (
                <div key={mi}>
                  {mod.modLabel && (
                    <div style={{ padding: "2px 12px 2px 20px", fontSize: "0.75em", color: "#6b7280" }}>
                      {mod.modLabel}
                    </div>
                  )}
                  {mod.features.map((f) => (
                    <label key={f.id} style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "3px 12px 3px 28px", cursor: "pointer",
                      background: selectedSet.has(f.id) ? "#eff6ff" : "transparent",
                      fontSize: "0.83em",
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(f.id)}
                        onChange={() => toggle(f.id)}
                      />
                      <code style={{ fontWeight: 600, color: "#1d4ed8" }}>{f.code}</code>
                      <span style={{ color: "#374151" }}>
                        {localeLabel(i18n.language, f) || f.labelEn}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Inline edit panel ─────────────────────────────────────────────────────────

function EditPanel({
  line,
  allFeatures,
  allEnvironments,
  initialFeatureIds,
  initialEnvironmentIds,
  busy,
  onSave,
  onCancel,
}: {
  line: BpmlLineDto;
  allFeatures: ApplicationFeatureDto[];
  allEnvironments: ApplicationEnvironmentDto[];
  initialFeatureIds: string[];
  initialEnvironmentIds: string[];
  busy: boolean;
  onSave: (edit: EditState) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<EditState>({
    lineId: line.id,
    label: line.label ?? "",
    complexityIndex: line.complexityIndex,
    isCritical: line.isCritical,
    processCategory: line.processCategory ?? "",
    featureIds: initialFeatureIds,
    environmentIds: initialEnvironmentIds,
  });

  return (
    <div style={{
      background: "#f0f9ff", border: "1px solid #bae6fd",
      borderRadius: 6, padding: "14px 16px", marginBottom: 4,
    }}>
      <div style={{ fontWeight: 600, fontSize: "0.85em", color: "#0369a1", marginBottom: 10 }}>
        ✎ {line.domain} / {line.macroProcess} / {line.businessProcess}
        {line.subProcess ? ` / ${line.subProcess}` : ""}
        {line.task ? ` / ${line.task}` : ""}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        {/* Label */}
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="sm-muted" style={{ display: "block", marginBottom: 4, fontSize: "0.82em" }}>{t("label")}</label>
          <input
            className="sm-input"
            style={{ width: "100%" }}
            placeholder="Libellé métier"
            value={edit.label}
            onChange={(e) => setEdit({ ...edit, label: e.target.value })}
          />
        </div>

        {/* Complexity */}
        <div>
          <label className="sm-muted" style={{ display: "block", marginBottom: 4, fontSize: "0.82em" }}>Complexité (1-10)</label>
          <input
            className="sm-input"
            type="number" min={1} max={10}
            value={edit.complexityIndex}
            onChange={(e) => setEdit({ ...edit, complexityIndex: Number(e.target.value) })}
            style={{ width: "100%" }}
          />
        </div>

        {/* Category */}
        <div>
          <label className="sm-muted" style={{ display: "block", marginBottom: 4, fontSize: "0.82em" }}>Catégorie</label>
          <select
            className="sm-input"
            style={{ width: "100%" }}
            value={edit.processCategory}
            onChange={(e) => setEdit({ ...edit, processCategory: e.target.value })}
          >
            <option value="">— aucune —</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Critical */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: "0.85em" }}>
            <input
              type="checkbox"
              checked={edit.isCritical}
              onChange={(e) => setEdit({ ...edit, isCritical: e.target.checked })}
            />
            {t("critical_col")}
          </label>
        </div>
      </div>

      {/* Features */}
      <div style={{ marginBottom: 10 }}>
        <label className="sm-muted" style={{ display: "block", marginBottom: 4, fontSize: "0.82em" }}>
          {t("features")} ({edit.featureIds.length} sélectionnée{edit.featureIds.length > 1 ? "s" : ""})
        </label>
        <FeaturePicker
          allFeatures={allFeatures}
          selectedIds={edit.featureIds}
          onChange={(ids) => setEdit({ ...edit, featureIds: ids })}
        />
      </div>

      {/* Environments */}
      {allEnvironments.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <label className="sm-muted" style={{ display: "block", marginBottom: 6, fontSize: "0.82em" }}>
            {t("environments_title")} — cocher les environnements couverts par cette ligne
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {allEnvironments.map((env) => (
              <label key={env.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.85em", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={edit.environmentIds.includes(env.id)}
                  onChange={(e) => {
                    const ids = e.target.checked
                      ? [...edit.environmentIds, env.id]
                      : edit.environmentIds.filter((id) => id !== env.id);
                    setEdit({ ...edit, environmentIds: ids });
                  }}
                />
                <span style={{
                  background: "#dbeafe", color: "#1d4ed8",
                  padding: "1px 8px", borderRadius: 10, fontWeight: 500,
                }}>
                  {env.applicationLabel} / {env.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="sm-form-actions">
        <button
          type="button"
          className="sm-btn sm-btn-primary"
          disabled={busy}
          onClick={() => onSave(edit)}
        >
          {t("save")}
        </button>
        <button type="button" className="sm-btn sm-btn-ghost" onClick={onCancel} disabled={busy}>
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export function BpmlEnrichmentPage() {
  const { t } = useTranslation();

  const [versionId, setVersionId] = useState<string | null>(null);
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [domainFilter, setDomainFilter] = useState("");
  const [lines, setLines] = useState<BpmlLineDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<BpmlEnrichBulkReport | null>(null);

  const [childParentId, setChildParentId] = useState("");
  const [childSub, setChildSub] = useState("");
  const [childTask, setChildTask] = useState("");
  const [criticalOnly, setCriticalOnly] = useState(false);

  // Features : toutes les features dispo + par ligne
  const [allFeatures, setAllFeatures] = useState<ApplicationFeatureDto[]>([]);
  const [lineFeatures, setLineFeatures] = useState<Record<string, ApplicationFeatureDto[]>>({});

  // Environments
  const [allEnvironments, setAllEnvironments] = useState<ApplicationEnvironmentDto[]>([]);
  const [lineEnvironments, setLineEnvironments] = useState<Record<string, ApplicationEnvironmentDto[]>>({});

  // Ligne en cours d'édition
  const [editingId, setEditingId] = useState<string | null>(null);

  const visibleLines = criticalOnly ? lines.filter((l) => l.isCritical) : lines;
  const criticalCount = lines.filter((l) => l.isCritical).length;

  async function load() {
    setError(null);
    try {
      const [domRes, active, appRes] = await Promise.all([
        domainsApi.list(),
        bpmlApi.listActiveLines(domainFilter || undefined),
        applicationApi.list(),
      ]);
      setDomains(domRes.domains);
      setVersionId(active.versionId);
      setLines(active.lines);
      void loadFeaturesForLines(active.lines);
      void loadEnvironmentsForLines(active.lines);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setLines([]);
      setVersionId(null);
    }
  }

  async function loadAllFeatures() {
    try {
      const res = await applicationFeaturesApi.list();
      setAllFeatures(res.features);
    } catch { /* silent */ }
  }

  async function loadAllEnvironments() {
    try {
      const res = await applicationEnvironmentsApi.list(undefined, false);
      setAllEnvironments(res.environments);
    } catch { /* silent */ }
  }

  async function loadEnvironmentsForLines(ls: BpmlLineDto[]) {
    if (ls.length === 0) return;
    try {
      const pairs = await Promise.all(
        ls.map((l) => applicationEnvironmentsApi.getForLine(l.id).then((r) => ({ id: l.id, envs: r.environments })))
      );
      const map: Record<string, ApplicationEnvironmentDto[]> = {};
      for (const p of pairs) map[p.id] = p.envs;
      setLineEnvironments(map);
    } catch { /* silent */ }
  }

  async function loadFeaturesForLines(ls: BpmlLineDto[]) {
    if (ls.length === 0) return;
    try {
      const pairs = await Promise.all(
        ls.map((l) =>
          applicationFeaturesApi.getByLine(l.id).then((r) => [l.id, r.features] as const)
        )
      );
      setLineFeatures(Object.fromEntries(pairs));
    } catch { /* non-critique */ }
  }

  useEffect(() => {
    void load();
    void loadAllFeatures();
    void loadAllEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainFilter]);

  async function saveLine(line: BpmlLineDto, patch: Parameters<typeof bpmlApi.patchLine>[1]) {
    setBusy(true);
    setError(null);
    try {
      await bpmlApi.patchLine(line.id, patch);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(edit: EditState) {
    setBusy(true);
    setError(null);
    try {
      // 1. Patch attributs de la ligne
      await bpmlApi.patchLine(edit.lineId, {
        label: edit.label.trim() || null,
        complexityIndex: edit.complexityIndex,
        isCritical: edit.isCritical,
        processCategory: edit.processCategory as "Process" | "Data" | "Reporting" | "Integration" | undefined || undefined,
      });
      // 2. Remplace les features
      await bpmlApi.setLineFeatures(edit.lineId, edit.featureIds);
      // 3. Remplace les environnements
      await applicationEnvironmentsApi.setForLine(edit.lineId, edit.environmentIds);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onBulk(event: FormEvent) {
    event.preventDefault();
    if (!file) { setError(t("choose_xlsx")); return; }
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const result = await bpmlApi.enrichBulk(file);
      setReport(result);
      setFile(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onClearDomain() {
    if (!domainFilter) { setError(t("select_domain_reset")); return; }
    if (!window.confirm(t("confirm_reset_enrichment"))) return;
    setBusy(true);
    setError(null);
    try {
      await bpmlApi.clearEnrichment(domainFilter);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onAddChild(event: FormEvent) {
    event.preventDefault();
    if (!versionId || !childParentId) { setError(t("select_parent_line")); return; }
    setBusy(true);
    setError(null);
    try {
      await bpmlApi.addChild(versionId, {
        parentId: childParentId,
        subProcess: childSub.trim() || undefined,
        task: childTask.trim() || undefined,
      });
      setChildSub("");
      setChildTask("");
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplate() {
    try {
      const blob = await bpmlApi.downloadEnrichTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "skillmap-bpml-enrich-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("enrichment_title")}</h1>
          <p>{t("enrichment_help")}</p>
        </div>
      </div>

      {error ? <div className="sm-alert sm-alert-error" role="alert">{error}</div> : null}

      {!versionId ? (
        <p className="sm-muted">{t("no_data")}</p>
      ) : (
        <>
          {/* Toolbar */}
          <div className="sm-toolbar" style={{ marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <select
              className="sm-input"
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              style={{ maxWidth: 200 }}
            >
              <option value="">{t("all_domains")}</option>
              {domains.map((d) => (
                <option key={d.id} value={d.code}>{d.code}</option>
              ))}
            </select>
            <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate()}>
              ⬇ {t("download_excel_template")}
            </button>
            <button
              type="button"
              className="sm-btn sm-btn-ghost"
              disabled={busy || !domainFilter}
              onClick={() => void onClearDomain()}
            >
              {t("reset_enrichment")}
            </button>
          </div>

          {/* Import Excel */}
          <div className="sm-form-card">
            <h2>{t("import")}</h2>
            <p className="sm-muted" style={{ marginBottom: 8 }}>{t("enrich_import_merge_hint")}</p>
            <form onSubmit={onBulk}>
              <div className="sm-form-actions">
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
                  {t("import")}
                </button>
              </div>
            </form>
            {report ? (
              <div className="sm-section" style={{ marginTop: 12 }}>
                <p className="sm-muted">
                  {t("updated_n")} : <strong>{report.updated}</strong> —{" "}
                  {t("created_n")} : <strong>{report.created}</strong> —{" "}
                  {t("errors_n")} : <strong>{report.errors.length}</strong>
                </p>
                {report.errors.length > 0 ? (
                  <ul className="sm-alert sm-alert-error" style={{ marginTop: 8, paddingLeft: 24 }}>
                    {report.errors.map((e) => (
                      <li key={`${e.row}-${e.message}`}>{t("excel_row", { row: e.row })} {e.message}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Ajouter sous-ligne */}
          <div className="sm-form-card">
            <h2>{t("add_child")}</h2>
            <form onSubmit={onAddChild}>
              <div className="sm-form-actions" style={{ flexWrap: "wrap" }}>
                <select
                  className="sm-input"
                  required
                  value={childParentId}
                  onChange={(e) => setChildParentId(e.target.value)}
                  style={{ minWidth: 280 }}
                >
                  <option value="">{t("parent_line_ph")}</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.domain} / {l.macroProcess} / {l.businessProcess}
                      {l.subProcess ? ` / ${l.subProcess}` : ""}
                      {l.task ? ` / ${l.task}` : ""}
                    </option>
                  ))}
                </select>
                <input className="sm-input" placeholder={t("sub_process")} value={childSub} onChange={(e) => setChildSub(e.target.value)} />
                <input className="sm-input" placeholder={t("task_optional")} value={childTask} onChange={(e) => setChildTask(e.target.value)} />
                <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>{t("add")}</button>
              </div>
            </form>
          </div>

          {/* Table des lignes */}
          <div className="sm-section">
            <div className="sm-toolbar" style={{ justifyContent: "space-between", marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>
                {t("line_count")} ({visibleLines.length}
                {criticalOnly ? "" : ` · ${t("critical_n", { count: criticalCount })}`})
              </h2>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={criticalOnly} onChange={(e) => setCriticalOnly(e.target.checked)} />
                {t("critical_only")}
              </label>
            </div>
            <p className="sm-muted" style={{ marginTop: 0 }}>{t("critical_hint")}</p>

            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>{t("critical_col")}</th>
                    <th>{t("domain")}</th>
                    <th>Macro</th>
                    <th>BP</th>
                    <th>Catégorie</th>
                    <th>Sub</th>
                    <th>Task</th>
                    <th>Cplx</th>
                    <th>{t("label")}</th>
                    <th style={{ minWidth: 180 }}>{t("features")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLines.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="sm-muted">
                        {criticalOnly ? t("no_critical_lines") : t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    visibleLines.map((line) => (
                      <>
                        {/* Ligne principale */}
                        <tr
                          key={line.id}
                          className={editingId === line.id ? "sm-row-editing" : undefined}
                        >
                          <td style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: busy ? "default" : "pointer", whiteSpace: "nowrap" }}>
                              <input
                                type="checkbox"
                                checked={line.isCritical}
                                disabled={busy}
                                onChange={(e) => void saveLine(line, { isCritical: e.target.checked })}
                              />
                              {line.isCritical
                                ? <span className="sm-badge">{t("yes")}</span>
                                : <span className="sm-muted">{t("no")}</span>}
                            </label>
                          </td>
                          <td>{line.domain}</td>
                          <td>{line.macroProcess}</td>
                          <td>{line.businessProcess}</td>
                          <td>{line.processCategory ?? "—"}</td>
                          <td>{line.subProcess ?? "—"}</td>
                          <td>{line.task ?? "—"}</td>
                          <td style={{ fontWeight: 600 }}>{line.complexityIndex}</td>
                          <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={line.label ?? undefined}>
                            {line.label ?? <span className="sm-muted">—</span>}
                          </td>
                          <td style={{ maxWidth: 200 }}>
                            {(lineFeatures[line.id] ?? []).length > 0 ? (
                              <span
                                className="sm-muted"
                                style={{ fontSize: "0.8em", lineHeight: 1.4 }}
                                title={(lineFeatures[line.id] ?? []).map((f) => `${f.code} — ${f.labelEn}`).join("\n")}
                              >
                                {(lineFeatures[line.id] ?? []).map((f) => f.code).join(" · ")}
                              </span>
                            ) : (
                              <span className="sm-muted">—</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="sm-link"
                              disabled={busy}
                              onClick={() => setEditingId(editingId === line.id ? null : line.id)}
                            >
                              {editingId === line.id ? t("cancel") : t("edit")}
                            </button>
                          </td>
                        </tr>

                        {/* Panneau d'édition inline */}
                        {editingId === line.id ? (
                          <tr key={`edit-${line.id}`}>
                            <td colSpan={11} style={{ padding: "4px 8px 10px" }}>
                              <EditPanel
                                line={line}
                                allFeatures={allFeatures}
                                allEnvironments={allEnvironments}
                                initialFeatureIds={(lineFeatures[line.id] ?? []).map((f) => f.id)}
                                initialEnvironmentIds={(lineEnvironments[line.id] ?? []).map((e) => e.id)}
                                busy={busy}
                                onSave={(edit) => void saveEdit(edit)}
                                onCancel={() => setEditingId(null)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
